/**
 * Pure helpers for the ticket rail's Details panel: which statuses the Status
 * select offers, and the "Received via" line. The API is the authority — the
 * allowed set comes from its state machine (`detail.allowedTransitions`).
 */

/** Resolve and reopen have their own dialogs; closing is the system's. */
const DEDICATED = new Set(['resolved', 'closed'])

/**
 * The Status select's targets: the API's `allowedTransitions` minus resolved /
 * closed. Empty (no select) for a customer, a terminal ticket, or an API that
 * sent none.
 */
export function statusTargets(allowed: readonly string[] | null | undefined, current: string): string[] {
  return [...new Set(allowed ?? [])].filter((s) => s !== current && !DEDICATED.has(s))
}

/** Every status, in the order the menu lists them. */
export const STATUS_MENU_ORDER = [
  'new',
  'in_progress',
  'waiting_on_customer',
  'waiting_on_vendor',
  'scheduled',
  'escalated_engineering',
  'resolved',
  'closed',
] as const

/**
 * What picking a status does: nothing (it is the current one), a plain move,
 * the Resolve dialog (a reason and a GxP assessment), the Reopen dialog (a
 * resolved ticket goes back to In progress through it), or nothing because it
 * is not reachable — with why.
 */
export type StatusOption =
  | { status: string; kind: 'current' }
  | { status: string; kind: 'move' }
  | { status: string; kind: 'resolve' }
  | { status: string; kind: 'reopen' }
  | { status: string; kind: 'blocked'; reason: 'closed_by_system' | 'terminal' | 'not_from_here' | 'reopen_first' | 'no_permission' }

/**
 * The status menu: every status, each with what picking it does. The API's
 * `allowedTransitions` stays the authority for plain moves; resolve and reopen
 * keep their dialogs; `closed` is only ever set by the system.
 */
export function statusOptions(
  current: string,
  allowed: readonly string[] | null | undefined,
  can: { update: boolean; resolve: boolean; reopen: boolean },
): StatusOption[] {
  const moves = new Set(allowed ?? [])
  return STATUS_MENU_ORDER.map((status): StatusOption => {
    if (status === current) return { status, kind: 'current' }
    if (status === 'closed') return { status, kind: 'blocked', reason: 'closed_by_system' }
    if (current === 'closed') return { status, kind: 'blocked', reason: 'terminal' }
    // A resolved ticket goes back to work through Reopen, which lands on In progress.
    if (current === 'resolved') {
      if (status !== 'in_progress') return { status, kind: 'blocked', reason: 'reopen_first' }
      return can.reopen ? { status, kind: 'reopen' } : { status, kind: 'blocked', reason: 'no_permission' }
    }
    if (status === 'resolved')
      return can.resolve ? { status, kind: 'resolve' } : { status, kind: 'blocked', reason: 'no_permission' }
    if (!can.update) return { status, kind: 'blocked', reason: 'no_permission' }
    return moves.has(status) ? { status, kind: 'move' } : { status, kind: 'blocked', reason: 'not_from_here' }
  })
}

/** "21 Oct 2026, 07:55" in the viewer's zone; null for a missing or malformed instant. */
export function formatReceivedAt(iso: string | null | undefined, timeZone: string): string | null {
  if (!iso) return null
  const at = Date.parse(iso)
  if (!Number.isFinite(at)) return null
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(at)
}
